'use client';
import { Text } from '@weareconceptstudio/core';
import { useEffect, useMemo, useRef } from 'react';
import gsap from 'gsap/all';
import { Draggable } from 'gsap/Draggable';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

//* Components
import ItemCategories from './Item';

//* HOC's
import { withUIContext } from '@/context';

//* Style
import CategoriesStyle from './style';

//! Register Draggable
gsap.registerPlugin(Draggable, ScrollTrigger);

const clamp01 = (v) => Math.max(0, Math.min(1, v));

const Categories = ({ winWidth, categories }) => {
	const sectionRef = useRef();
	const downCategories = useRef();
	const upCategories = useRef();
	const wrapper = useRef();
	const scrollTriggerRef = useRef();
	const draggableUp = useRef();
	const draggableDown = useRef();

	useEffect(() => {
		if (!categories || categories.length === 0) return;

		const sectionEl = sectionRef.current;
		const upEl = upCategories.current;
		const downEl = downCategories.current;
		if (!sectionEl || !upEl || !downEl) return;

		const sizeTranslate = upEl.offsetWidth - winWidth;
		if (sizeTranslate <= 0) return;

		const state = { isDragging: false };
		let dragAnchor = null;

		const setFromProgress = (progress) => {
			const p = clamp01(progress);
			gsap.set(upEl, { x: -sizeTranslate * p });
			gsap.set(downEl, { x: sizeTranslate * p });
		};

		const syncRowsFromUpX = (upX) => {
			gsap.set(upEl, { x: upX });
			gsap.set(downEl, { x: -upX });
		};

		const getVisualProgress = (upX) => clamp01(-upX / sizeTranslate);

		const getProgressFromScroll = (scrollProgress) => {
			if (!dragAnchor) return scrollProgress;

			const { scrollProgress: anchorScroll, visualProgress: anchorVisual } = dragAnchor;
			const p = scrollProgress;

			if (p >= anchorScroll) {
				if (anchorScroll >= 1) return anchorVisual;
				return anchorVisual + (1 - anchorVisual) * ((p - anchorScroll) / (1 - anchorScroll));
			}

			if (anchorScroll <= 0) return 0;
			return anchorVisual * (p / anchorScroll);
		};

		const resetDragState = (self) => {
			dragAnchor = null;
			if (!state.isDragging && self) {
				setFromProgress(self.progress);
			}
		};

		scrollTriggerRef.current = ScrollTrigger.create({
			id: 'home-categories-scroll',
			trigger: sectionEl,
			start: 'top bottom',
			end: 'bottom top',
			scrub: true,
			onUpdate: (self) => {
				if (state.isDragging) return;
				setFromProgress(getProgressFromScroll(self.progress));
			},
			onEnter: resetDragState,
			onEnterBack: resetDragState,
			onLeave: () => {
				dragAnchor = null;
			},
			onLeaveBack: () => {
				dragAnchor = null;
			},
		});

		const createRowDraggable = (item, direction = 1) =>
			Draggable.create(item, {
				type: 'x',
				bounds:
					direction === 1
						? { minX: -sizeTranslate, maxX: 0 }
						: { minX: 0, maxX: sizeTranslate },
				inertia: true,
				allowNativeTouchScrolling: true,
				onDragStart: () => {
					state.isDragging = true;
				},
				onDrag: function () {
					syncRowsFromUpX(direction * this.x);
				},
				onDragEnd: function () {
					state.isDragging = false;
					const upX = gsap.getProperty(upEl, 'x') || 0;
					dragAnchor = {
						scrollProgress: scrollTriggerRef.current.progress,
						visualProgress: getVisualProgress(upX),
					};
				},
			});

		draggableUp.current = createRowDraggable(upEl, 1);
		draggableDown.current = createRowDraggable(downEl, -1);

		return () => {
			scrollTriggerRef.current?.kill();
			draggableUp.current?.forEach((d) => d.kill());
			draggableDown.current?.forEach((d) => d.kill());
		};
	}, [winWidth, categories]);

	const [upDataJSX, downDataJSX] = useMemo(() => {
		if (!categories || categories.length === 0) return [[], []];

		const list = categories.length % 2 !== 0 ? [...categories, categories[0]] : categories;
		const middleIndex = Math.ceil(list.length / 2);

		const upData = list.slice(0, middleIndex).map((item, i) => (
			<ItemCategories
				key={`up-${i}`}
				data={item}
				reverse
			/>
		));

		const downData = list.slice(middleIndex).map((item, i) => (
			<ItemCategories
				key={`down-${i}`}
				data={item}
				reverse
			/>
		));

		return [upData, downData];
	}, [categories]);

	return (
		<CategoriesStyle
			ref={sectionRef}
			className={`${categories ? 'categories' : ' '}`}>
			<div className='wrapper-title'>
				<Text
					tag={'h2'}
					className={'h2 title-categories uppercase'}
					text={'ourCategories'}
				/>
			</div>

			<div
				className='wrapper-data-categories'
				ref={wrapper}>
				<div
					className='data-wrapper up-data'
					ref={upCategories}>
					{upDataJSX}
				</div>
				<div className='wrapper-down'>
					<div
						className='data-wrapper down-data'
						ref={downCategories}>
						{downDataJSX}
					</div>
				</div>
			</div>
		</CategoriesStyle>
	);
};

export default withUIContext(Categories, ['winWidth']);
